import React, { useState } from 'react';
import { Box, Button, Grid, TextField, FormControlLabel, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions } from '@material-ui/core';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router';
import { Alert } from '@mui/material';

import LocationObject from 'entities/locationObject/model';
import { isEin, isEmail, isLocationObject } from 'utils/validators';
import { FormLocationObjectField } from 'retired/FormLocationObjectField/FormLocationObjectField';
import DialogButton from 'retired/DialogButton';
import {
  ChapterRequestQuery,
  NonprofitIdentifierTypeEnum,
  RoleCode,
  useAcceptChapterRequestMutation,
  useUpdateChapterRequestFromReviewMutation,
} from 'generated/graphql';

import NonprofitsList from '../NonprofitsList';

interface ChapterRequestFormData {
  parentName: string;
  name: string;
  mailingAddress?: LocationObject;
  emailAddress: string;
  ein: string;
  adminEmail: string;
  adminFirstName: string;
  adminLastName: string;
  addAdditionalAdmin: boolean;
  requesterPreferredName: string;
  requestedByName: string;
  requestedRoles: string;
}

const ChapterRequestForm = ({
  chapterRequest,
  onReject,
  refetch,
}: {
  chapterRequest: NonNullable<ChapterRequestQuery['chapterRequest']>;
  onReject: () => void;
  refetch: () => Promise<any>;
}) => {
  const {
    id,
    parentNonprofitId,
    nonprofitName,
    locationObject,
    users,
    email,
    identifiers,
    requestedByName,
    parentName,
    zendeskTicketId,
  } = chapterRequest;

  const emptyUser = {
    roles: [],
    email: '',
    fullName: {
      first: '',
      last: '',
    },
  };

  const user = users.length > 0 ? users[0] : emptyUser;

  const { fullName, email: adminEmail, roles } = user && 'fullName' in user ? user : emptyUser;
  const ein = identifiers[0]?.id || '';

  const {
    control,
    formState: { errors, touchedFields },
    getValues,
    watch,
  } = useForm<ChapterRequestFormData>({
    defaultValues: {
      parentName: `${parentName} (${parentNonprofitId})`,
      name: nonprofitName,
      mailingAddress: {
        address: locationObject.address || undefined,
        city: locationObject.city || undefined,
        state: locationObject.state || undefined,
        stateCode: locationObject.stateCode || undefined,
        postalCode: locationObject.postalCode || undefined,
        country: locationObject.country || undefined,
        countryCode: locationObject.countryCode || undefined,
        geo: locationObject.geo ? { 
          type: locationObject.geo.type || undefined,
          coordinates: locationObject.geo.coordinates 
        } : undefined,
      },
      emailAddress: email,
      ein,
      adminEmail,
      adminFirstName: fullName.first || '',
      adminLastName: fullName.last || '',
      requesterPreferredName: requestedByName || '',
      requestedRoles: roles.join(', '),
      addAdditionalAdmin: users.length > 0,
    },
    mode: 'onChange',
  });

  const history = useHistory();
  const [acceptChapterRequest, { loading: accepting }] = useAcceptChapterRequestMutation();
  const [updateChapterRequest, { loading: updating }] = useUpdateChapterRequestFromReviewMutation();
  const [validationMessages, setValidationMessages] = useState<Array<{ message: string; type: string }>>([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const preValidationMessages = chapterRequest.validationMessages || [];

  const preValidationErrors = preValidationMessages.filter(msg => msg && msg.type === 'error');
  const preValidationInfos = preValidationMessages.filter(msg => msg && msg.type === 'info');
  const hasErrors = preValidationErrors.length > 0;

  const rejectButton = (
    <Button
      color="primary"
      type="button"
      variant="outlined"
      style={{ marginRight: 8 }}
      onClick={onReject}
    >
      Reject
    </Button>
  );

  const updateButton = (
    <Button
      color="primary"
      type="button"
      variant="contained"
      style={{ marginRight: 8 }}
      disabled={updating}
      onClick={async () => {
        const formValues = getValues();
        const {
          emailAddress,
          ein: formValuesEin,
          mailingAddress,
          name,
          adminEmail: formValuesAdminEmail,
          adminFirstName,
          adminLastName,
          addAdditionalAdmin,
        } = formValues;

        try {
          const response = await updateChapterRequest({
            variables: {
              id,
              input: {
                email: emailAddress,
                identifiers: [{ type: NonprofitIdentifierTypeEnum.Ein, id: formValuesEin }],
                locationObject: mailingAddress,
                nonprofitName: name,
                users: addAdditionalAdmin
                  ? [
                      {
                        email: formValuesAdminEmail,
                        roles: [RoleCode.Verified, RoleCode.Administrator, RoleCode.Owner],
                        fullName: {
                          first: adminFirstName,
                          last: adminLastName,
                        },
                      },
                    ]
                  : [],
              },
            },
          });

          if (response.data?.updateChapterRequest) {
            setUpdateSuccess(true);
            // Refetch the query to get fresh validation messages
            await refetch();
          }
        } catch (error) {
          console.error('Failed to update chapter request:', error);
        }
      }}
    >
      {updating ? 'Updating...' : 'Update Request'}
    </Button>
  );

  const acceptButton = hasErrors ? (
    <Button
      color="primary"
      variant="contained"
      style={{ backgroundColor: '#f44336' }}
      onClick={() => setShowErrorModal(true)}
    >
      Accept
    </Button>
  ) : (
    <DialogButton
      title={`Are you sure you want to accept "${nonprofitName}" chapter request?`}
      text="Once confirmed it will create a new nonprofit."
      confirmLabel="Confirm"
      cancelLabel="Cancel"
      buttonProps={{ variant: 'contained', disabled: accepting }}
      handleConfirm={async () => {
        const formValues = getValues();
        const {
          emailAddress,
          ein: formValuesEin,
          mailingAddress,
          name,
          adminEmail: formValuesAdminEmail,
          adminFirstName,
          adminLastName,
          addAdditionalAdmin,
        } = formValues;

        const response = await acceptChapterRequest({
          variables: {
            id,
            hash: '',
            input: {
              email: emailAddress,
              identifiers: [{ type: NonprofitIdentifierTypeEnum.Ein, id: formValuesEin }],
              locationObject: mailingAddress,
              nonprofitName: name,
              users: addAdditionalAdmin
                ? [
                    {
                      email: formValuesAdminEmail,
                      roles: [RoleCode.Verified, RoleCode.Administrator, RoleCode.Owner],
                      fullName: {
                        first: adminFirstName,
                        last: adminLastName,
                      },
                    },
                  ]
                : [],
            },
          },
        });

        const result = response.data?.acceptChapterRequest;
        if (result?.success) {
          history.goBack();
        } else if (result?.validationMessages && result.validationMessages.length > 0) {
          setValidationMessages(result.validationMessages.filter(msg => msg).map(msg => ({ message: msg!.message, type: msg!.type })));
        }
      }}
    >
      Accept
    </DialogButton>
  );

  return (
    <form>
      <fieldset>
        <Box style={{ paddingTop: 0, marginBottom: 30 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
            }}
          >
            <h2>
              Review chapter request{' '}
              <a
                href={`https://joindeed.zendesk.com/agent/tickets/${zendeskTicketId}`}
                target="_blank"
              >
                #{zendeskTicketId}
              </a>
            </h2>
          </div>

          {preValidationInfos.length > 0 && (
            <Alert severity="info" style={{ marginBottom: 16 }}>
              <strong>Pre-validation Information:</strong>
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                {preValidationInfos.map((message, index) => (
                  <li key={index}>{message?.message}</li>
                ))}
              </ul>
            </Alert>
          )}

          {preValidationErrors.length > 0 && (
            <Alert severity="error" style={{ marginBottom: 16 }}>
              <strong>Pre-validation Errors:</strong>
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                {preValidationErrors.map((message, index) => (
                  <li key={index}>{message?.message}</li>
                ))}
              </ul>
              <p style={{ marginTop: 8, marginBottom: 0 }}>
                <strong>Note:</strong> This request has validation errors. You cannot accept it in this state. Use the "Update Request" button to correct these issues.
              </p>
            </Alert>
          )}

          {updateSuccess && (
            <Alert severity="success" style={{ marginBottom: 16 }}>
              Chapter request updated successfully!
            </Alert>
          )}

          {validationMessages.length > 0 && (
            <Alert severity="error" style={{ marginBottom: 16 }}>
              <strong>The chapter request was processed with the following issues:</strong>
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                {validationMessages.map((error, index) => (
                  <li key={index}>{error.message}</li>
                ))}
              </ul>
              {validationMessages.some(message => message.type === 'error') && (
                <p style={{ marginTop: 8, marginBottom: 0 }}>
                  The request has validation errors. You cannot accept it in this state. Use the "Update Request" button to correct these issues.
                </p>
              )}
            </Alert>
          )}
          <Grid container spacing={4}>
            <Grid
              item
              sm={12}
              lg={6}
              style={{ display: 'flex', gap: '24px', flexDirection: 'column' }}
            >
              <Controller
                name="parentName"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Parent organization" fullWidth required disabled />
                )}
              />

              <Controller
                name="name"
                control={control}
                rules={{
                  required: 'This field is required',
                  maxLength: { value: 100, message: 'Please use less than 100 characters' },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Chapter name"
                    fullWidth
                    required
                    error={Boolean(errors.name)}
                    helperText={touchedFields.name && errors.name?.message}
                  />
                )}
              />

              <div>
                <Controller
                  name="mailingAddress"
                  control={control}
                  rules={{
                    required: 'This field is required',
                    validate: isLocationObject(),
                  }}
                  render={({ field }) => (
                    <FormLocationObjectField
                      input={field}
                      meta={{
                        error: errors.mailingAddress?.message || '',
                        touched: Boolean(touchedFields.mailingAddress),
                      }}
                      label="Mailing address"
                      sx={{ textTransform: 'uppercase' }}
                      fullWidth
                      afterSelect={() => {}}
                      helperText={touchedFields.mailingAddress && errors.mailingAddress?.message}
                    />
                  )}
                />
              </div>

              <Controller
                name="emailAddress"
                control={control}
                rules={{
                  required: 'This field is required',
                  validate: isEmail(),
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Chapter Email address"
                    fullWidth
                    required
                    error={Boolean(errors.emailAddress)}
                    helperText={touchedFields.emailAddress && errors.emailAddress?.message}
                  />
                )}
              />

              <Controller
                name="ein"
                control={control}
                rules={{
                  required: 'This field is required',
                  validate: isEin(),
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Chapter EIN / tax ID"
                    fullWidth
                    required
                    error={Boolean(errors.ein)}
                    helperText={touchedFields.ein && errors.ein?.message}
                  />
                )}
              />

              <Box>
                <Alert severity="info" style={{ marginBottom: '16px' }}>
                  All owners for the parent organization will automatically be added to the chapter.
                </Alert>

                <Controller
                  name="addAdditionalAdmin"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={value}
                          onChange={(event) => {
                            onChange(event.target.checked);
                          }}
                        />
                      }
                      label="Add an additional user as an owner"
                    />
                  )}
                />
              </Box>

              {watch('addAdditionalAdmin') && (
                <>
                  <Controller
                    name="adminEmail"
                    control={control}
                    rules={{
                      required: 'This field is required',
                      validate: isEmail(),
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Chapter admin email address"
                        fullWidth
                        required
                        error={Boolean(errors.adminEmail)}
                        helperText={
                          (touchedFields.emailAddress && errors.emailAddress?.message) ||
                          'Tip: By default, all active Owners for the parent organization will automatically be added as Owners for any chapters created. (if you share an EIN)'
                        }
                      />
                    )}
                  />
                  <Controller
                    name="adminFirstName"
                    control={control}
                    rules={{
                      required: 'This field is required',
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Chapter admin first name"
                        fullWidth
                        required
                        error={Boolean(errors.adminFirstName)}
                        helperText={touchedFields.adminFirstName && errors.adminFirstName?.message}
                      />
                    )}
                  />

                  <Controller
                    name="adminLastName"
                    control={control}
                    rules={{
                      required: 'This field is required',
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Chapter admin last name"
                        fullWidth
                        required
                        error={Boolean(errors.adminLastName)}
                        helperText={touchedFields.adminLastName && errors.adminLastName?.message}
                      />
                    )}
                  />
                </>
              )}

              <Controller
                name="requesterPreferredName"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Requested by" fullWidth disabled />
                )}
              />

              <Controller
                name="requestedRoles"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Requested roles" fullWidth disabled />
                )}
              />
            </Grid>
            <Grid item sm={12} lg={6}>
              <NonprofitsList nonprofitName={nonprofitName} />
            </Grid>
          </Grid>
          <div style={{ marginTop: 30, display: 'flex', justifyContent: 'end' }}>
            {rejectButton}
            {updateButton}
            {acceptButton}
          </div>
        </Box>
      </fieldset>

      <Dialog open={showErrorModal} onClose={() => setShowErrorModal(false)}>
        <DialogTitle style={{ color: '#f44336' }}>Cannot Accept Chapter Request</DialogTitle>
        <DialogContent>
          <p>This chapter request has validation errors that must be resolved before it can be accepted:</p>
          <ul style={{ marginTop: 16 }}>
            {preValidationErrors.map((error, index) => (
              <li key={index} style={{ marginBottom: 8 }}>
                <strong>{error?.message}</strong>
              </li>
            ))}
          </ul>
          <p style={{ marginTop: 16 }}>Please resolve these issues before attempting to accept the request.</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowErrorModal(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </form>
  );
};

export default ChapterRequestForm;
